import { useState, useMemo, useEffect } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Table as TableComponent,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Search,
  MoreVertical,
  Mail,
  Phone,
  Users,
  UserPlus,
  Pencil,
  Filter,
  Trash2,
  MailX,
  RefreshCw,
  StickyNote,
  Megaphone,
  MessageSquare,
  Ban,
  AlertTriangle,
  Wheat,
  Upload,
  Download,
  Crown,
  Sparkles,
  Star,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useEstablishment } from "@/contexts/EstablishmentContext";
import { calculateClientStats, filterClients, sortClients, LoyaltyLevel } from "@/lib/clientUtils";
import { useClientsWithNotes } from "@/hooks/useClientsWithNotes";
import { CustomerData } from "@/types/client";
import { Reservation, Table, TableAvailability, TableWeekdayAvailability, CombinedTable } from "@/types/table";
import { ClientReservationsDialog } from "./ClientReservationsDialog";
import { EditClientDialog } from "./EditClientDialog";
import { DeleteClientDialog } from "./DeleteClientDialog";
import { ImportClientsDialog } from "./ImportClientsDialog";
import { exportClientsToCSV } from "@/lib/csvExportClientsUtils";

interface ClientsViewProps {
  reservations: Reservation[];
  onReservationsUpdate: (reservations: Reservation[]) => void;
  timeSlots: { start: string; end: string; }[];
  allTables: Table[];
  allReservations: Array<{
    id: number;
    tableId: string | undefined;
    combinedTableId: string | undefined;
    time: string;
    reservationDate: Date;
  }>;
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  combinedTables: CombinedTable[];
  onReservationUpdate: (reservationId: number, updates: {
    customerName: string;
    customerEmail?: string;
    customerPhone: string;
    numberOfPeople: number;
    tableId: string;
    date: Date;
    startTime: string;
    notes?: string;
    dietaryPreferences?: string[];
    internalNotes?: string;
    status?: "planifié" | "arrivé" | "annulé" | "en_attente";
  }) => void;
}

const LoyaltyBadge = ({ level, size = 'sm' }: { level: LoyaltyLevel; size?: 'sm' | 'md' }) => {
  if (level === 'none') return null;
  const iconClass = size === 'sm' ? 'h-2 w-2 mr-1' : 'h-3 w-3 mr-1';
  switch (level) {
    case 'vip':
      return (
        <Badge className="bg-amber-500/15 text-amber-700 border-amber-300 dark:text-amber-400 dark:border-amber-600 text-xs">
          <Crown className={iconClass} /> VIP
        </Badge>
      );
    case 'loyal':
      return (
        <Badge className="bg-purple-500/15 text-purple-700 border-purple-300 dark:text-purple-400 dark:border-purple-600 text-xs">
          <Sparkles className={iconClass} /> Fidèle
        </Badge>
      );
    case 'returning':
      return (
        <Badge className="bg-blue-500/15 text-blue-700 border-blue-300 dark:text-blue-400 dark:border-blue-600 text-xs">
          <Star className={iconClass} /> Régulier
        </Badge>
      );
    default:
      return null;
  }
};

export function ClientsView({ 
  reservations, 
  onReservationsUpdate, 
  timeSlots,
  allTables,
  allReservations,
  availabilities,
  weekdayAvailabilities,
  combinedTables,
  onReservationUpdate,
}: ClientsViewProps) {
  const { establishmentId } = useEstablishment();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"firstName" | "lastName" | "reservations" | "lastVisit">("lastVisit");
  const [visitFilter, setVisitFilter] = useState<"all" | "returning" | "loyal" | "vip" | "unsubscribed" | "marketing" | "blacklisted" | "noshow">("all");
  const [editingClient, setEditingClient] = useState<CustomerData | null>(null);
  const [viewingClientReservations, setViewingClientReservations] = useState<CustomerData | null>(null);
  const [deletingClient, setDeletingClient] = useState<CustomerData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [unsubscribedEmails, setUnsubscribedEmails] = useState<Set<string>>(new Set());
  const [loadingUnsubscribes, setLoadingUnsubscribes] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;

  // Charger les emails désabonnés
  useEffect(() => {
    if (!establishmentId) return;
    
    const fetchUnsubscribes = async () => {
      setLoadingUnsubscribes(true);
      const { data, error } = await supabase
        .from('email_unsubscribes')
        .select('email')
        .eq('establishment_id', establishmentId);
      
      if (!error && data) {
        setUnsubscribedEmails(new Set(data.map(u => u.email.toLowerCase())));
      }
      setLoadingUnsubscribes(false);
    };
    
    fetchUnsubscribes();
  }, [establishmentId]);

  const handleResubscribe = async (email: string) => {
    try {
      const { error } = await supabase
        .from('email_unsubscribes')
        .delete()
        .eq('email', email)
        .eq('establishment_id', establishmentId);

      if (error) {
        toast({
          title: "Erreur",
          description: "Impossible de réinscrire le client",
          variant: "destructive",
        });
        return;
      }

      setUnsubscribedEmails(prev => {
        const next = new Set(prev);
        next.delete(email.toLowerCase());
        return next;
      });

      toast({
        title: "Client réinscrit",
        description: "Le client recevra à nouveau les emails marketing",
      });
    } catch (error) {
      console.error('Error resubscribing client:', error);
    }
  };

  // Use clients with notes from database
  const { clients, refetch: refetchClients } = useClientsWithNotes({ reservations });

  const stats = useMemo(() => {
    return calculateClientStats(clients);
  }, [clients]);

  const filteredAndSortedClients = useMemo(() => {
    let filtered = filterClients(clients, searchTerm);
    
    // Appliquer le filtre par nombre de visites ou statut
    if (visitFilter === "returning") {
      filtered = filtered.filter(client => client.totalReservations >= 2);
    } else if (visitFilter === "loyal") {
      filtered = filtered.filter(client => client.totalReservations >= 3);
    } else if (visitFilter === "vip") {
      filtered = filtered.filter(client => client.totalReservations >= 5);
    } else if (visitFilter === "unsubscribed") {
      filtered = filtered.filter(client => unsubscribedEmails.has(client.email.toLowerCase()));
    } else if (visitFilter === "marketing") {
      filtered = filtered.filter(client => client.marketingConsent === true);
    } else if (visitFilter === "blacklisted") {
      filtered = filtered.filter(client => client.isBlacklisted === true);
    } else if (visitFilter === "noshow") {
      filtered = filtered.filter(client => client.hasNoShow === true);
    }
    
    return sortClients(filtered, sortBy);
  }, [clients, searchTerm, sortBy, visitFilter, unsubscribedEmails]);

  const unsubscribedCount = useMemo(() => {
    return clients.filter(client => client.email && unsubscribedEmails.has(client.email.toLowerCase())).length;
  }, [clients, unsubscribedEmails]);

  const marketingConsentCount = useMemo(() => {
    return clients.filter(client => client.marketingConsent === true).length;
  }, [clients]);

  const existingClientEmails = useMemo(() => {
    return new Set(clients.filter(c => c.email).map(c => c.email.toLowerCase()));
  }, [clients]);

  // Pre-compute loyalty map to avoid O(n²) in render
  const loyaltyMap = useMemo(() => {
    const map = new Map<string, LoyaltyLevel>();
    clients.forEach(client => {
      if (!client.email) return;
      const key = client.email.toLowerCase();
      if (client.arrivedReservations >= 10) map.set(key, 'vip');
      else if (client.arrivedReservations >= 5) map.set(key, 'loyal');
      else if (client.arrivedReservations >= 2) map.set(key, 'returning');
    });
    return map;
  }, [clients]);

  // Reset page on filter/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, visitFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSortedClients.length / PAGE_SIZE));
  const paginatedClients = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredAndSortedClients.slice(start, start + PAGE_SIZE);
  }, [filteredAndSortedClients, currentPage, PAGE_SIZE]);

  const handleClientUpdate = async (oldEmail: string, updates: Partial<CustomerData>) => {
    try {
      // If we have the customer ID, use it directly
      let customerId = updates.id;
      
      // If not, find the customer by old email (only if email is valid)
      if (!customerId) {
        if (!oldEmail || oldEmail.startsWith('__no_email__')) {
          toast({
            title: "Erreur",
            description: "Client introuvable dans la base de données (pas d'identifiant)",
            variant: "destructive",
          });
          return;
        }
        const { data: customer, error: fetchError } = await supabase
          .from('customers')
          .select('id')
          .eq('email', oldEmail)
          .eq('establishment_id', establishmentId)
          .maybeSingle();
        
        if (fetchError || !customer) {
          toast({
            title: "Erreur",
            description: "Client introuvable dans la base de données",
            variant: "destructive",
          });
          return;
        }
        customerId = customer.id;
      }
      
      // Note: Email updates are handled by the EditClientDialog via edge function
      // Here we only handle non-email field updates
      const normalizedNewEmail = updates.email?.toLowerCase().trim();
      const normalizedOldEmail = (oldEmail || "").toLowerCase();
      const emailChanged = normalizedNewEmail && normalizedNewEmail !== normalizedOldEmail;
      
      // If email changed, it was already handled by the edge function in EditClientDialog
      // We just need to update other fields
      const { error: updateError } = await supabase
        .from('customers')
        .update({
          first_name: updates.firstName,
          last_name: updates.lastName,
          phone: updates.phone,
          notes: updates.notes?.trim() || null,
          civility: updates.civility || null,
          company: updates.company || null,
          professional_email: updates.professionalEmail || null,
          professional_phone: updates.professionalPhone || null,
          birthdate: updates.birthdate || null,
          address: updates.address || null,
          city: updates.city || null,
          zip: updates.zip || null,
          country: updates.country || null,
          lang: updates.lang || null,
          guest_status: updates.guestStatus || null,
          is_blacklisted: updates.isBlacklisted ?? false,
          has_no_show: updates.hasNoShow ?? false,
          allergies_notes: updates.allergiesNotes || null,
          allergies_tags: updates.allergiesTags || [],
          guest_tags: updates.guestTags || [],
          ...(emailChanged ? { email: normalizedNewEmail } : {}),
          updated_at: new Date().toISOString(),
        })
        .eq('id', customerId);
      
      if (updateError) {
        toast({
          title: "Erreur",
          description: "Impossible de mettre à jour le client",
          variant: "destructive",
        });
        return;
      }
      
      // Update local state - use the new email if changed
      const newEmail = emailChanged ? normalizedNewEmail : oldEmail;
      const updatedReservations = reservations.map(res => 
        res.customerEmail?.toLowerCase() === oldEmail.toLowerCase() 
          ? { 
              ...res, 
              customerFirstName: updates.firstName !== undefined ? updates.firstName : res.customerFirstName,
              customerLastName: updates.lastName !== undefined ? updates.lastName : res.customerLastName,
              customerEmail: newEmail || res.customerEmail,
              customerPhone: updates.phone || res.customerPhone
            }
          : res
      );
      onReservationsUpdate(updatedReservations);
      
      // Refetch customer notes to update any changes
      refetchClients();
      
      toast({
        title: "Succès",
        description: "Les informations du client ont été mises à jour",
      });
    } catch (error) {
      console.error('Error updating client:', error);
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite",
        variant: "destructive",
      });
    }
  };

  const handleDeleteClient = async (email: string) => {
    setIsDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke('delete-client', {
        body: { email }
      });

      if (error) {
        toast({
          title: "Erreur",
          description: "Impossible de supprimer le client",
          variant: "destructive",
        });
        return;
      }

      // Mettre à jour le state local en filtrant les réservations du client
      const updatedReservations = reservations.filter(
        r => r.customerEmail.toLowerCase() !== email.toLowerCase()
      );
      onReservationsUpdate(updatedReservations);
      
      toast({
        title: "Client supprimé",
        description: `${data.deleted.reservations} réservation(s) supprimée(s)`,
      });
      
      setDeletingClient(null);
    } catch (error) {
      console.error('Error deleting client:', error);
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm font-medium">
              <span className="hidden sm:inline">Total Clients</span>
              <span className="sm:hidden">Clients</span>
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground hidden sm:block" />
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="text-xl sm:text-2xl font-bold">{stats.totalClients}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm font-medium">
              <span className="hidden sm:inline">Nouveaux ce mois</span>
              <span className="sm:hidden">Nouveaux</span>
            </CardTitle>
            <UserPlus className="h-4 w-4 text-muted-foreground hidden sm:block" />
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="text-xl sm:text-2xl font-bold">{stats.newClientsThisMonth}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm font-medium">
              <span className="hidden sm:inline">Désabonnés</span>
              <span className="sm:hidden">Désab.</span>
            </CardTitle>
            <MailX className="h-4 w-4 text-muted-foreground hidden sm:block" />
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="text-xl sm:text-2xl font-bold">{unsubscribedCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom, email ou téléphone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                className="pl-10"
              />
            </div>

            <Select value={visitFilter} onValueChange={(value: any) => setVisitFilter(value)}>
              <SelectTrigger className="w-full md:w-[200px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filtrer par visites" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les clients</SelectItem>
                <SelectItem value="returning">Revenus (2+ visites)</SelectItem>
                <SelectItem value="loyal">Fidèles (3+ visites)</SelectItem>
                <SelectItem value="vip">VIP (5+ visites)</SelectItem>
                <SelectItem value="unsubscribed">
                  <span className="flex items-center gap-2">
                    <MailX className="h-3 w-3" />
                    Désabonnés ({unsubscribedCount})
                  </span>
                </SelectItem>
                <SelectItem value="marketing">
                  <span className="flex items-center gap-2">
                    <Megaphone className="h-3 w-3" />
                    Consentement marketing ({marketingConsentCount})
                  </span>
                </SelectItem>
                <SelectItem value="blacklisted">
                  <span className="flex items-center gap-2">
                    <Ban className="h-3 w-3" />
                    Blacklistés
                  </span>
                </SelectItem>
                <SelectItem value="noshow">
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="h-3 w-3" />
                    No-show
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Trier par" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lastVisit">Dernière visite</SelectItem>
                <SelectItem value="reservations">Nb. réservations</SelectItem>
                <SelectItem value="lastName">Nom de famille</SelectItem>
                <SelectItem value="firstName">Prénom</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Card>
        <CardHeader className="px-4 sm:px-6 flex flex-row items-center justify-between">
          <CardTitle className="text-base sm:text-lg">Liste des clients ({filteredAndSortedClients.length})</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowImportDialog(true)}>
              <Upload className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Importer</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportClientsToCSV(filteredAndSortedClients)} disabled={filteredAndSortedClients.length === 0}>
              <Download className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Exporter</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-2 sm:px-6">
          {/* Mobile view - Card list */}
          <div className="sm:hidden space-y-3">
            {paginatedClients.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">Aucun client trouvé</p>
            ) : (
              paginatedClients.map((client) => (
                <Card key={client.id || client.email} className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{client.firstName} {client.lastName}</span>
                        {client.notes && (
                          <Tooltip>
                            <TooltipTrigger>
                              <StickyNote className="h-3 w-3 text-amber-500" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[250px]">
                              <p className="text-xs whitespace-pre-wrap">{client.notes}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {client.marketingConsent && (
                          <Tooltip>
                            <TooltipTrigger>
                              <Megaphone className="h-3 w-3 text-primary" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">Consentement e-mail donné</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {client.smsConsent && (
                          <Tooltip>
                            <TooltipTrigger>
                              <MessageSquare className="h-3 w-3 text-primary" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">Consentement SMS donné</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {client.isBlacklisted && (
                          <Badge variant="destructive" className="text-xs">
                            <Ban className="h-2 w-2 mr-1" />
                            Blacklisté
                          </Badge>
                        )}
                        {client.hasNoShow && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/20 text-xs">
                            <AlertTriangle className="h-2 w-2 mr-1" />
                            No-show
                          </Badge>
                        )}
                        {(client.allergiesTags && client.allergiesTags.length > 0) && (
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge variant="outline" className="text-xs">
                                <Wheat className="h-2 w-2 mr-1" />
                                Allergies
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">{client.allergiesTags.join(', ')}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {unsubscribedEmails.has(client.email.toLowerCase()) && (
                          <Badge variant="outline" className="text-orange-600 border-orange-300 bg-orange-50 text-xs">
                            <MailX className="h-2 w-2 mr-1" />
                            Désab.
                          </Badge>
                        )}
                        <LoyaltyBadge level={loyaltyMap.get(client.email?.toLowerCase()) || 'none'} size="sm" />
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{client.email}</p>
                      <p className="text-xs text-muted-foreground">{client.phone}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <Badge variant="secondary" className="text-xs">
                          {client.totalReservations} résa.
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {(() => { try { return format(client.lastReservationDate, "d MMM yyyy", { locale: fr }); } catch { return '—'; } })()}
                        </span>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="shrink-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setViewingClientReservations(client)}>
                          <Users className="w-4 h-4 mr-2" />
                          Voir réservations
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setEditingClient(client)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Modifier
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a href={`tel:${client.phone}`} className="flex items-center cursor-pointer">
                            <Phone className="w-4 h-4 mr-2" />
                            Appeler
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a href={`mailto:${client.email}`} className="flex items-center cursor-pointer">
                            <Mail className="w-4 h-4 mr-2" />
                            Envoyer email
                          </a>
                        </DropdownMenuItem>
                        {unsubscribedEmails.has(client.email.toLowerCase()) && (
                          <DropdownMenuItem onClick={() => handleResubscribe(client.email)}>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Réinscrire
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setDeletingClient(client)} className="text-destructive focus:text-destructive">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </Card>
              ))
            )}
          </div>
          {/* Desktop view - Table */}
          <div className="rounded-md border hidden sm:block">
            <TableComponent>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead className="text-center">Réservations</TableHead>
                  <TableHead>Dernière visite</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Aucun client trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedClients.map((client) => (
                    <TableRow key={client.id || client.email}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {client.firstName} {client.lastName}
                          {client.notes && (
                            <Tooltip>
                              <TooltipTrigger>
                                <StickyNote className="h-4 w-4 text-amber-500" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[300px]">
                                <p className="font-medium mb-1">Note client :</p>
                                <p className="text-sm whitespace-pre-wrap">{client.notes}</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {client.marketingConsent && (
                            <Tooltip>
                              <TooltipTrigger>
                                <Megaphone className="h-4 w-4 text-primary" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Consentement e-mail donné</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {client.smsConsent && (
                            <Tooltip>
                              <TooltipTrigger>
                                <MessageSquare className="h-4 w-4 text-primary" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Consentement SMS donné</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {client.isBlacklisted && (
                            <Badge variant="destructive" className="text-xs">
                              <Ban className="h-3 w-3 mr-1" />
                              Blacklisté
                            </Badge>
                          )}
                          {client.hasNoShow && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/20 text-xs">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              No-show
                            </Badge>
                          )}
                          {(client.allergiesTags && client.allergiesTags.length > 0) && (
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge variant="outline" className="text-xs">
                                  <Wheat className="h-3 w-3 mr-1" />
                                  Allergies
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{client.allergiesTags.join(', ')}</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {unsubscribedEmails.has(client.email.toLowerCase()) && (
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge variant="outline" className="text-orange-600 border-orange-300 bg-orange-50">
                                  <MailX className="h-3 w-3 mr-1" />
                                  Désabonné
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Ce client ne reçoit plus les emails marketing</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          <LoyaltyBadge level={loyaltyMap.get(client.email?.toLowerCase()) || 'none'} size="md" />
                        </div>
                      </TableCell>
                      <TableCell>{client.email}</TableCell>
                      <TableCell>{client.phone}</TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setViewingClientReservations(client)}
                          className="h-8"
                        >
                          <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/80">
                            {client.totalReservations}
                          </Badge>
                        </Button>
                      </TableCell>
                      <TableCell>
                        {(() => { try { return format(client.lastReservationDate, "d MMM yyyy", { locale: fr }); } catch { return '—'; } })()}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditingClient(client)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <a
                                href={`tel:${client.phone}`}
                                className="flex items-center cursor-pointer"
                              >
                                <Phone className="w-4 h-4 mr-2" />
                                Appeler
                              </a>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <a
                                href={`mailto:${client.email}`}
                                className="flex items-center cursor-pointer"
                              >
                                <Mail className="w-4 h-4 mr-2" />
                                Envoyer email
                              </a>
                            </DropdownMenuItem>
                            {unsubscribedEmails.has(client.email.toLowerCase()) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleResubscribe(client.email)}>
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                  Réinscrire aux emails
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => setDeletingClient(client)}
                              className="text-destructive focus:text-destructive focus:bg-destructive/10"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </TableComponent>
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} sur {totalPages} ({filteredAndSortedClients.length} clients)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  Suivant
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {editingClient && (
        <EditClientDialog
          open={!!editingClient}
          onOpenChange={(open) => {
            if (!open) setEditingClient(null);
          }}
          client={editingClient}
          onClientUpdate={handleClientUpdate}
        />
      )}

      {viewingClientReservations && (
        <ClientReservationsDialog
          open={!!viewingClientReservations}
          onOpenChange={(open) => {
            if (!open) setViewingClientReservations(null);
          }}
          client={viewingClientReservations}
          timeSlots={timeSlots}
          allTables={allTables}
          allReservations={allReservations}
          availabilities={availabilities}
          weekdayAvailabilities={weekdayAvailabilities}
          combinedTables={combinedTables}
          existingClients={clients}
          onReservationUpdate={onReservationUpdate}
          onClientUpdate={handleClientUpdate}
        />
      )}

      <DeleteClientDialog
        client={deletingClient}
        open={!!deletingClient}
        onOpenChange={(open) => {
          if (!open) setDeletingClient(null);
        }}
        onConfirm={handleDeleteClient}
        isLoading={isDeleting}
      />

      <ImportClientsDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        existingEmails={existingClientEmails}
        onImportComplete={refetchClients}
      />
    </div>
  );
}
